import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { FormikContext } from "formik";
import { ContentWithImage, SelectControl, Text } from "..";
import { applyKey, distinctArray, mapToOptions } from "../../utils";
import * as S from "./ResultadoSorteiosEmprestimos.Style";
import { parseISO } from "../../utils/dateUtils";

const SEGURO_HABITACIONAL_MAIS_PREMIAVEL = "SeguroHabitacionalMaisPremiavel";

const NumeroSorteado = ({ resultados, productId }) => {
  const numeroSorteado =
    !Array.isArray(resultados) && resultados
      ? resultados.combinacoes[0].combinacoes_sorteadas[0].descricao
      : "";

  return (
    <>
      {Array.isArray(resultados) ? (
        applyKey(resultados || [], (resultado, idx) => (
          <S.NumeroSorteadoContainer>
            <S.RowContainer>
              <S.NumeroSorteadoLabel as={Text} textType="caption">
                {resultados?.length >= 2 ? `${idx + 1}º ` : null}Número sorteado
              </S.NumeroSorteadoLabel>
              <S.NumeroSorteado
                as={Text}
                textType="headline"
                nivel="2"
                styleAs="1"
              >
                {resultado.combinacoes[0].combinacoes_sorteadas[0].descricao}
              </S.NumeroSorteado>
            </S.RowContainer>
            {productId === SEGURO_HABITACIONAL_MAIS_PREMIAVEL && idx === 0 && (
              <Text>
                Você concorre com esse número durante o primeiro ano do seu
                financiamento.
              </Text>
            )}
            {productId === SEGURO_HABITACIONAL_MAIS_PREMIAVEL && idx === 1 && (
              <Text>
                Você concorre com esse número durante toda a vigência do seu
                financiamento.
              </Text>
            )}
          </S.NumeroSorteadoContainer>
        ))
      ) : (
        <S.RowContainer className="resultado">
          <S.NumeroSorteadoLabel as={Text} elementType="p" textSize="sm">
            Número sorteado
          </S.NumeroSorteadoLabel>
          <S.NumeroSorteado as={Text} textType="headline" nivel="2" styleAs="1">
            {numeroSorteado}
          </S.NumeroSorteado>
        </S.RowContainer>
      )}
    </>
  );
};

NumeroSorteado.defaultProps = {
  resultados: null,
};

NumeroSorteado.propTypes = {
  resultados: PropTypes.oneOfType([PropTypes.object]),
  productId: PropTypes.string.isRequired,
};

const ResultadoSorteiosEmprestimos = ({
  resultados,
  dataSorteios,
  error,
  anoResultado,
  imgSrc,
  imgAlt,
  setAnoResultado,
}) => {
  const { setFieldValue, submitForm, values } = useContext(FormikContext);
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    if (dataSorteios) {
      const arrayDates = [...dataSorteios]
        .map((dataSorteio) => parseISO(dataSorteio))
        .reverse();
      setSelectedDates(arrayDates);
    }
  }, [dataSorteios]);

  const selectAnos = useMemo(
    () =>
      mapToOptions(
        distinctArray(
          selectedDates.map((dataSorteio) => dataSorteio.getFullYear())
        )
      ),
    [selectedDates]
  );

  const fetchResultadoDate = useCallback(
    (dataExtracao) => {
      setFieldValue("dataBusca", dataExtracao);

      if (dataExtracao) {
        submitForm();
      }
    },
    [setFieldValue, submitForm]
  );

  const getAvaliableDates = useCallback(
    (ano) =>
      selectedDates
        .filter(
          (dataSorteio) => dataSorteio.getFullYear() === parseInt(ano, 10)
        )
        .map((data) => ({
          label: data.toLocaleDateString("pt-br", {
            month: "2-digit",
            day: "2-digit",
          }),
          value: data.toISOString().substring(0, 10),
        })),
    [selectedDates]
  );

  const selectDatasSorteios = useMemo(() => {
    return mapToOptions(getAvaliableDates(anoResultado));
  }, [anoResultado, getAvaliableDates]);

  const onSelectDataBusca = useCallback(
    (evtDate) => {
      const dataExtracao = [...dataSorteios].find((dataSorteio) =>
        evtDate.includes(dataSorteio)
      );

      fetchResultadoDate(dataExtracao);
    },
    [dataSorteios, fetchResultadoDate]
  );

  const selectAnoHandle = useCallback(
    (ano) => {
      setAnoResultado(ano);

      const { value: dataExtracao } = getAvaliableDates(ano)[0];

      fetchResultadoDate(dataExtracao);
    },
    [fetchResultadoDate, getAvaliableDates, setAnoResultado]
  );

  return (
    <ContentWithImage imgSrc={imgSrc} imgAlt={imgAlt} imgColumnSize="6">
      {resultados ? (
        <>
          <S.RowContainer>
            <S.SelectAno
              as={SelectControl}
              options={selectAnos}
              label="Ano"
              name="Ano da data do sorteio"
              onChange={({ currentTarget: { value } }) =>
                selectAnoHandle(value)
              }
            />
            <S.SelectData
              as={SelectControl}
              options={selectDatasSorteios}
              label="Data"
              name="Dia e mês do sorteio"
              onChange={({ currentTarget: { value } }) =>
                onSelectDataBusca(value)
              }
            />
          </S.RowContainer>
          <NumeroSorteado resultados={resultados} productId={values.id} />
          <Text>
            Caso o seu número da sorte seja contemplado,{" "}
            <strong>entraremos em contato com você.</strong>
          </Text>
        </>
      ) : null}

      {error && (
        <S.ErrorText as={Text} textSize="lg" bodyStrong>
          <strong>
            Nosso sistema está indisponível no momento. Tente novamente mais
            tarde
          </strong>
        </S.ErrorText>
      )}
    </ContentWithImage>
  );
};

ResultadoSorteiosEmprestimos.defaultProps = {
  resultados: null,
  dataSorteios: [],
  error: null,
};

ResultadoSorteiosEmprestimos.propTypes = {
  resultados: PropTypes.arrayOf(Object),
  dataSorteios: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.bool,
  anoResultado: PropTypes.string.isRequired,
  imgSrc: PropTypes.string.isRequired,
  imgAlt: PropTypes.string.isRequired,
  setAnoResultado: PropTypes.func.isRequired,
};

export default ResultadoSorteiosEmprestimos;
